/**
 * CURATED FIRST
 * Shopify Horizon Theme
 *
 * Supported URL formats:
 *
 * ?first=product-handle
 *
 * ?first=/products/product-handle
 *
 * ?first=https://yourstore.com/products/product-handle
 *
 * ?first=https://yourstore.com/products/product-handle?variant=123456789
 *
 * PURPOSE:
 * Take the product clicked in an ad and place that exact product
 * as the first card in a Shopify collection.
 *
 * Works with:
 * - Static ads using a product handle
 * - Meta catalog ads using product.url
 * - Products already visible on page 1
 * - Products located on later collection pages
 * - Products not normally inside the landing collection
 */

(() => {
  'use strict';

  const PARAM_NAME = 'first';
  const CARD_SELECTOR = 'li.product-grid__item';
  const MAX_PAGES_TO_SEARCH = 20;

  const currentUrl = new URL(window.location.href);
  const rawFirst = currentUrl.searchParams.get(PARAM_NAME);

  /*
   * Normal collection visit.
   * If there is no ?first= parameter, do absolutely nothing.
   */
  if (!rawFirst) return;

  /*
   * If the shopper has already used Shopify filtering or sorting,
   * allow Horizon to behave normally instead of forcing the ad product.
   */
  const hasCustomerFiltering = Array.from(
    currentUrl.searchParams.keys()
  ).some((key) => {
    return key === 'sort_by' || key.startsWith('filter.');
  });

  if (hasCustomerFiltering) return;

  /*
   * Turn the value after ?first= into a Shopify product handle.
   *
   * Accepts:
   * vintage-mic-tee
   *
   * /products/vintage-mic-tee
   *
   * https://store.com/products/vintage-mic-tee
   *
   * Encoded Meta product URLs
   */
  function getTarget(rawValue) {
    if (!rawValue) return null;

    let value = rawValue.trim();

    /*
     * Meta may URL-encode product.url.
     * Decode safely up to two times.
     */
    for (let i = 0; i < 2; i++) {
      try {
        const decoded = decodeURIComponent(value);

        if (decoded === value) break;

        value = decoded;
      } catch (error) {
        break;
      }
    }

    /*
     * Static ad format:
     * ?first=vintage-mic-tee
     */
    if (/^[a-z0-9][a-z0-9-]*$/i.test(value)) {
      return {
        handle: value.toLowerCase(),
        variant: null,
      };
    }

    /*
     * Full or relative Shopify product URL.
     */
    try {
      const productUrl = new URL(value, window.location.origin);

      const match = productUrl.pathname.match(
        /\/products\/([^/?#]+)/i
      );

      if (match) {
        return {
          handle: decodeURIComponent(match[1]).toLowerCase(),
          variant: productUrl.searchParams.get('variant'),
        };
      }
    } catch (error) {
      /*
       * Continue to fallback parser.
       */
    }

    /*
     * Final fallback.
     */
    const match = value.match(
      /\/products\/([^/?#&]+)/i
    );

    if (!match) return null;

    return {
      handle: decodeURIComponent(match[1]).toLowerCase(),
      variant: null,
    };
  }

  const target = getTarget(rawFirst);

  if (!target || !target.handle) return;

  /*
   * Extract the product handle from a Shopify product link.
   */
  function handleFromHref(href) {
    if (!href) return null;

    try {
      const url = new URL(
        href,
        window.location.origin
      );

      const match = url.pathname.match(
        /\/products\/([^/?#]+)/i
      );

      return match
        ? decodeURIComponent(match[1]).toLowerCase()
        : null;
    } catch (error) {
      return null;
    }
  }

  /*
   * Determine whether a Horizon product card belongs
   * to the product requested through ?first=.
   */
  function cardMatchesTarget(card) {
    if (!(card instanceof Element)) return false;

    if (
      card.dataset.curatedFirstHandle &&
      card.dataset.curatedFirstHandle === target.handle
    ) {
      return true;
    }

    const links = card.querySelectorAll('a[href]');

    for (const link of links) {
      const handle = handleFromHref(
        link.getAttribute('href')
      );

      if (handle === target.handle) {
        return true;
      }
    }

    return false;
  }

  /*
   * Find the requested product card in the supplied
   * document or HTML element.
   */
  function findTargetCard(root = document) {
    const cards = root.querySelectorAll(
      CARD_SELECTOR
    );

    for (const card of cards) {
      if (cardMatchesTarget(card)) {
        return card;
      }
    }

    return null;
  }

  /*
   * Find Horizon's actual product grid.
   */
  function getProductGrid() {
    const resultsList =
      document.querySelector('results-list');

    /*
     * Horizon normally uses ref="grid".
     */
    const horizonGrid =
      resultsList?.querySelector('[ref="grid"]') ||
      document.querySelector('#ResultsList');

    if (horizonGrid) {
      return horizonGrid;
    }

    /*
     * Fallback:
     * find the parent containing the product cards.
     */
    const firstCard =
      document.querySelector(CARD_SELECTOR);

    return firstCard?.parentElement || null;
  }

  /*
   * Move an existing product card to position #1.
   */
  function putCardFirst(card) {
    const grid = getProductGrid();

    if (!grid || !card) return false;

    const firstCard = Array.from(
      grid.children
    ).find((child) => {
      return child.matches?.(CARD_SELECTOR);
    });

    if (!firstCard) return false;

    if (card !== firstCard) {
      grid.insertBefore(
        card,
        firstCard
      );
    }

    card.dataset.curatedFirstPinned = 'true';
    card.dataset.curatedFirstHandle =
      target.handle;

    return true;
  }

  /*
   * Build a clean Shopify collection pagination URL.
   */
  function collectionPageUrl(
    pathname,
    pageNumber
  ) {
    const url = new URL(
      pathname,
      window.location.origin
    );

    url.searchParams.set(
      'page',
      String(pageNumber)
    );

    return url;
  }

  /*
   * Download one Shopify collection page
   * and convert it into a searchable HTML document.
   */
  async function fetchCollectionPage(
    pathname,
    pageNumber
  ) {
    const url = collectionPageUrl(
      pathname,
      pageNumber
    );

    try {
      const response = await fetch(
        url.toString(),
        {
          credentials: 'same-origin',
          headers: {
            'X-Requested-With':
              'XMLHttpRequest',
          },
        }
      );

      if (!response.ok) {
        return null;
      }

      const html =
        await response.text();

      return new DOMParser().parseFromString(
        html,
        'text/html'
      );
    } catch (error) {
      return null;
    }
  }

  /*
   * Search through collection pages until the
   * advertised product is found.
   */
  async function searchCollection(
    pathname,
    startPage = 1
  ) {
    let page = startPage;
    let detectedLastPage = null;

    while (
      page <= MAX_PAGES_TO_SEARCH &&
      (
        detectedLastPage === null ||
        page <= detectedLastPage
      )
    ) {
      const fetchedDocument =
        await fetchCollectionPage(
          pathname,
          page
        );

      if (!fetchedDocument) break;

      const fetchedGrid =
        fetchedDocument.querySelector(
          'results-list [ref="grid"]'
        ) ||
        fetchedDocument.querySelector(
          '#ResultsList'
        ) ||
        fetchedDocument.querySelector(
          CARD_SELECTOR
        )?.parentElement;

      if (!fetchedGrid) break;

      /*
       * If Horizon provides the final page number,
       * use it so unnecessary requests stop.
       */
      if (detectedLastPage === null) {
        const lastPage = Number(
          fetchedGrid.dataset.lastPage
        );

        if (
          Number.isFinite(lastPage) &&
          lastPage > 0
        ) {
          detectedLastPage = lastPage;
        }
      }

      /*
       * Check this fetched page for the
       * advertised product.
       */
      const targetCard =
        findTargetCard(
          fetchedDocument
        );

      if (targetCard) {
        return document.importNode(
          targetCard,
          true
        );
      }

      /*
       * No cards means we've reached the end.
       */
      const cards =
        fetchedDocument.querySelectorAll(
          CARD_SELECTOR
        );

      if (!cards.length) break;

      page += 1;
    }

    return null;
  }

  /*
   * If we inject a product from a later page,
   * Horizon's infinite scroll could eventually load
   * the original copy.
   *
   * This observer removes ONLY that duplicate.
   */
  function preventLaterDuplicate(
    pinnedCard
  ) {
    const grid = getProductGrid();

    if (!grid || !pinnedCard) return;

    const pinnedProductId =
      pinnedCard.dataset.productId || null;

    const observer =
      new MutationObserver(() => {
        /*
         * If Horizon replaced the grid because the
         * shopper sorted or filtered, stop watching.
         */
        if (
          !document.documentElement.contains(
            pinnedCard
          )
        ) {
          observer.disconnect();
          return;
        }

        const cards =
          grid.querySelectorAll(
            CARD_SELECTOR
          );

        for (const card of cards) {
          if (card === pinnedCard) {
            continue;
          }

          const sameProductId =
            pinnedProductId &&
            card.dataset.productId &&
            card.dataset.productId ===
              pinnedProductId;

          if (
            sameProductId ||
            cardMatchesTarget(card)
          ) {
            card.remove();
          }
        }
      });

    observer.observe(
      grid,
      {
        childList: true,
      }
    );
  }

  /*
   * Main Curated First function.
   */
  async function initCuratedFirst() {
    const grid = getProductGrid();

    if (!grid) return;

    /*
     * CASE 1
     *
     * Product is already visible on the current
     * collection page.
     *
     * Simply move that existing Horizon card
     * to position #1.
     */
    const existingCard =
      findTargetCard(document);

    if (existingCard) {
      putCardFirst(existingCard);
      return;
    }

    /*
     * CASE 2
     *
     * Product belongs to this collection,
     * but is on page 2, 3, 4, etc.
     */
    const currentPage =
      Number(
        currentUrl.searchParams.get('page')
      ) || 1;

    let fetchedCard = null;

    if (currentPage === 1) {
      fetchedCard =
        await searchCollection(
          window.location.pathname,
          2
        );
    } else {
      fetchedCard =
        await searchCollection(
          window.location.pathname,
          1
        );
    }

    /*
     * CASE 3
     *
     * Product isn't part of this collection.
     *
     * Search Shopify's /collections/all so a
     * catalog ad can still bring any available
     * Online Store product into card #1.
     */
    if (!fetchedCard) {
      const shopRoot =
        window.Shopify?.routes?.root &&
        window.Shopify.routes.root !== '/'
          ? window.Shopify.routes.root
          : '/';

      const allProductsPath =
        `${shopRoot}collections/all`;

      if (
        window.location.pathname !==
        allProductsPath
      ) {
        fetchedCard =
          await searchCollection(
            allProductsPath,
            1
          );
      }
    }

    /*
     * Product could not be located.
     * Leave the collection untouched.
     */
    if (!fetchedCard) {
      console.warn(
        `[Curated First] Product "${target.handle}" could not be found.`
      );

      return;
    }

    /*
     * Because we're inserting this product into the
     * landing collection, tell Horizon to treat it
     * as a page-1 product.
     */
    fetchedCard.dataset.page = '1';

    fetchedCard.dataset.curatedFirstInjected =
      'true';

    fetchedCard.dataset.curatedFirstHandle =
      target.handle;

/*
 * Curated/ad-injected products should behave as a clean
 * static collection card.
 *
 * Horizon product cards can include image-carousel arrows
 * and second-image hover behavior. Because this card was
 * injected after the collection loaded, those controls can
 * appear outside the image.
 *
 * Remove them only from the injected curated card.
 */
fetchedCard
  .querySelectorAll('slideshow-arrows')
  .forEach((arrows) => arrows.remove());

const curatedGallery =
  fetchedCard.querySelector('.card-gallery');

if (curatedGallery) {
  curatedGallery.removeAttribute('on:pointerenter');
  curatedGallery.removeAttribute('on:pointerleave');
}

const curatedSlideshow =
  fetchedCard.querySelector('slideshow-component');

if (curatedSlideshow) {
  curatedSlideshow.setAttribute('disabled', 'true');
}

    /*
     * Re-check the live grid after the network request.
     */
    const currentGrid =
      getProductGrid();

    if (!currentGrid) return;

    const currentFirstCard =
      Array.from(
        currentGrid.children
      ).find((child) => {
        return child.matches?.(
          CARD_SELECTOR
        );
      });

    if (!currentFirstCard) return;

    /*
     * Insert the advertised product directly
     * before the normal first product.
     */
    currentGrid.insertBefore(
      fetchedCard,
      currentFirstCard
    );

    fetchedCard.dataset.curatedFirstPinned =
      'true';

    /*
     * Prevent the same product from appearing
     * twice later through infinite scrolling.
     */
    preventLaterDuplicate(
      fetchedCard
    );
  }

  /*
   * Run once Horizon's collection HTML exists.
   */
  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initCuratedFirst,
      {
        once: true,
      }
    );
  } else {
    initCuratedFirst();
  }
})();